import * as React from 'react';
import {
    type AccessibilityProps,
    Animated,
    type ImageStyle,
    type ImageURISource,
    type StyleProp,
    // @ts-ignore
    unstable_createElement,
} from 'react-native';

import { useHead } from '@gitbook/util-react-head';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { ImageLoaderContext } from './ImageLoaderProvider';

export type ImageSource = ImageURISource;

/**
 * An Image primitive with SSR support
 */
export const Image = React.forwardRef(function ImageWithRef(
    props: AccessibilityProps & {
        /**
         * The URI to the image source (either a remote URL or a local file resource).
         */
        source: ImageSource;
        /**
         * A style object to apply to the image.
         */
        style?: StyleProp<ImageStyle>;
        /**
         * React element to render when the image failed to load.
         */
        errorFallback?: React.ReactNode;
        /**
         * Callback when the image has loaded.
         */
        onLoad?: () => void;

        /**
         * Used to locate this view in end-to-end tests.
         */
        testID?: string | undefined;

        /**
         * Custom data-* attributes to add to the image.
         * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dataset
         */
        dataSet?: { [dataKey: string]: string };

        /**
         * Indicate how to load the image.
         * @default 'eager'
         */
        loading?: 'eager' | 'lazy' | 'priority';
        height?: string;
        width?: string;
    },
    ref: React.Ref<HTMLImageElement>
) {
    const {
        source,
        style,
        errorFallback,
        onLoad,
        dataSet,
        loading: propLoading = 'eager',
        // @ts-ignore - forwardedRef is not used, but we need to remove it from the rest props because it's passed by createAnimatedComponent
        forwardedRef,
        accessibilityLabel,
        height,
        width,
        ...rest
    } = props;

    const { forceEagerLoading } = React.useContext(ImageLoaderContext);
    const [error, setError] = useStateWithDeps(false, [source.uri]);

    const loading = forceEagerLoading ? 'eager' : propLoading;

    const imageHeight = height || 'auto';
    const imageWidth = width || '100%';

    useHead((head) => {
        if (loading !== 'priority' || !source.uri) {
            return;
        }

        head.writeAsync({
            type: 'link',
            rel: 'preload',
            href: source.uri,
            as: 'image',
        });
    });

    if (error) {
        return errorFallback;
    }

    return unstable_createElement('img', {
        ...rest,
        alt: accessibilityLabel || '',
        ref,

        /**
         * @CAUTION browser compatibility
         *
         * Firefox compatibility
         * `loading` must precede `src` for it to work properly in Firefox. See:
         *  - https://bugzilla.mozilla.org/show_bug.cgi?id=1647077
         *  - https://github.com/facebook/react/issues/25883
         */
        loading: loading === 'lazy' ? loading : undefined,
        src: source.uri,

        width: imageWidth,
        height: imageHeight,
        style: [style],
        dataSet,
        decoding: 'async',
        onError: () => {
            if (errorFallback) {
                setError(true);
            }
        },
        onLoad,
    });
});

/**
 * Animated version of the Image component.
 */
export const ImageAnimated = Animated.createAnimatedComponent(Image);
